import { store } from 'state/_store'
import { getHasReseller } from 'state/Reseller'
import { getRestaurants } from 'state/Restaurants'
import Locale from 'utils/Locale'

/** @abstract */
export default class ChatPlatform {

	/** @type Account */ account
	/** @type User */ user
	/** @type boolean */ hasReseller
	/** @type Restaurant[] */ restaurants
	/** @type string */ locale

	constructor(account, user) {
		//comes from the argument since it's used in SupportChat to define the platform...
		this.account = account
		this.user    = user

		//...but the rest we can grab directly to avoid a bunch of extra args
		const state      = store.getState()
		this.hasReseller = getHasReseller(state)
		this.restaurants = getRestaurants(state)
		this.locale      = new Locale().current()
	}

	/**
	 * Should return the same as the class name (since `constructor.name` gets compacted during build)
	 * @abstract
	 * @return {string}
	 */
	get name() { return '' }

	/**
	 * An uniquely human-identifiable username, to be used as main ID for the chat user.
	 * Includes the Representative's username and ID.
	 * @final
	 */
	get completeUsername() { return `${this.user.username} (user #${this.user.id})` }

	/**
	 * Whether this platform requires their code all the times (if events are also sent up, for instance) or just when
	 * chat is requested.
	 * @return {boolean}
	 */
	get initOnStartup() { return false }

	/**
		* Whether this platform requires their {@link extraDetails} field names as "slugs" or human-readable text.
	 * @returns {boolean}
	 */
	get sluggifiedFields() { return false }

	/**
	 * Returns a bitwise value of the features supported by this platform.
	 * @example `return SupportChat.FEAT_PAGE_VIEWS | SupportChat.FEAT_EVENTS`
	 * @returns {number}
	 */
	get supportedFeatures() { return 0 }

	/**
	 * Starts up the chat integration (usually a script you copy-paste-adapt from their setup pages).
	 * There's no need to worry with multiple calls on this function, just do whatever you need to get it started :)
	 * @return {Promise<boolean>} Must return a promise that fulfills with true when the final step is done, or false in case of errors.
	 * @abstract
	 */
	init() {}

	/**
	 * Indicates whether the platform was initialized or not.
	 * @return boolean
	 * @abstract
	 */
	get isUp() { return false }

	/**
	 * Shows the chat interface (usually a bubble or window on the right).
	 * @abstract
	 */
	open() {}

	/**
	 * Hides/closes the chat interface, which can be reopened with pop (thus, won't just kill it)
	 * @abstract
	 */
	close() {}

	/**
	 * Destroys any initialized stuff. Will require reinitialization to show the popup again.
	 * @abstract
	 */
	nuke() {}

	/**
	 * Sends up a user event.
	 * @note If the platform doesn't support this, it's noop, so there's no need to implement the method.
	 * @param type {string} Anything identifiable by the platform
	 * @param payload {object} Anything extra to be attached to the event
	 */
	event(type, payload = {}) {}

	/**
	 * Sends up a "page view" event.
	 * @note If the platform doesn't support this, it's noop, so there's no need to implement the method.
	 */
	sendPageView() {}

	/**
	 * Extra fields to be included in the chat meta-data: mainly account and restaurant identifiers.
	 * These details are kinda duplicated in PHP's Account::syncToSegment()
	 * @return {{'Acc. ID', 'Acc. name', 'Rest. names', 'Rest. #'}|{'Restaurant name', siteID, 'Rest. names', 'Rest. #'}}
	 */
	get extraDetails() {
		//eHungry has different fields than Resellers should use
		const [keyAccId,     keyAccName,     keyRestNames, keyRestIds] = this.sluggifiedFields?
			    ['account-id', 'account-name', 'rest-names', 'rest-ids'] :
			    ['Acc. ID',    'Acc. name',    'Rest. names', 'Rest. #']

		return {
			[keyAccId]    : this.account.id_string,
			[keyAccName]  : this.account.display_name,
			[keyRestNames]: (this.restaurants.length == 1)?
				this.restaurants[0].display_name :
				this.restaurants.map(r => `#${r.id}: ${r.display_name}`).join(' || '),
			[keyRestIds]  : (this.restaurants.length == 1)?
				this.restaurants[0].id :
				this.restaurants.map(r => r.id).join(', ')
		}
	}
}
